import { useState } from "react";
import { useIntl } from "react-intl";

import { TenantListOrderByEnum, TenantRead } from "@nens/rana/api-v1";
import { TrashIcon } from "@nens/ranalib";

import { useRanaTableData } from "@/hooks/router/use-rana-table-data";
import { useInstancePermissions } from "@/queries/query-instance-permissions";
import { useTenantListPageQuery, useTenantQuery } from "@/queries/query-tenant";

type AdminTenantModalType = { type: "delete"; tenants: TenantRead[] } | null;

export const useAdminTenantList = () => {
  const intl = useIntl();

  const [modal, setModal] = useState<AdminTenantModalType>(null);

  const [isCreating, setIsCreating] = useState(false);

  const { data: tenant } = useTenantQuery();

  const { permissionsLoading, canDeleteTenant } = useInstancePermissions();

  const { page, offset, setPageSize, limit, setPage, orderings, orderingString, setOrdering } =
    useRanaTableData({
      keyAddon: "adminTenant",
      initialOrderings: { name: "az" },
    });

  const { data, isLoading } = useTenantListPageQuery({
    offset,
    limit,
    orderBy: (orderingString as TenantListOrderByEnum) || null,
  });

  const isSkeleton = isLoading || permissionsLoading;

  const tenantList = data?.items ?? [];

  const getTableActions = () => {
    return [
      {
        label: intl.formatMessage({
          id: "action.delete",
          defaultMessage: "Delete",
        }),
        action: (rowIds: string[]) => {
          const filteredTenants = tenantList.filter(({ id }) => rowIds.includes(id));

          setModal({
            type: "delete",
            tenants: filteredTenants,
          });
        },

        icon: <TrashIcon />,
      },
    ];
  };

  const getActions = (tenant: TenantRead) => {
    return [
      {
        icon: <TrashIcon />,
        label: intl.formatMessage({
          id: "action.delete",
          defaultMessage: "Delete",
        }),
        action: () => setModal({ type: "delete", tenants: [tenant] }),
      },
    ];
  };

  return {
    page,
    setPageSize,
    setPage,
    orderings,
    setOrdering,
    tenantList,
    isSkeleton,
    numPages: Math.ceil((data?.total ?? 0) / limit),
    limit,
    getTableActions,
    getActions,
    modal,
    canDeleteTenant,
    onClose: () => setModal(null),
    tenant,
    isCreating,
    setIsCreating,
  };
};
